import React, { useEffect, useState } from 'react';
import { VoucherWithCodes, CreateVoucherRequest, UpdateVoucherRequest } from '../../../../../types/voucher.types';
import Button from '../../../../components/common/Button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { Editor } from 'primereact/editor';
import './VoucherRewardForm.css';

interface VoucherRewardFormProps {
    visible: boolean;
    voucher?: VoucherWithCodes | null;
    onHide: () => void;
    onSubmit: (data: CreateVoucherRequest | UpdateVoucherRequest) => Promise<void>;
    loading?: boolean;
    isEdit?: boolean;
}

interface FormData {
    title: string;
    description: string;
    code: string;
    discount_type: 'value' | 'percentage';
    discount_value?: number;
    discount_percentage?: number;
    max_discount?: number;
    start_date: Date | null;
    end_date: Date | null;
    max_usage: number;
    minimum_amount?: number;
    voucher_type_vehicle: 'car' | 'motorbike' | 'both';
    point_cost: number;
    total_redemption_limit?: number;
    user_redemption_limit?: number;
}

const VoucherRewardForm: React.FC<VoucherRewardFormProps> = ({
    visible,
    voucher,
    onHide,
    onSubmit,
    loading = false,
    isEdit = false
}) => {
    const [formData, setFormData] = useState<FormData>({
        title: '',
        description: '',
        code: '',
        discount_type: 'percentage',
        discount_percentage: 10,
        max_discount: undefined,
        start_date: new Date(),
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
        max_usage: 1,
        voucher_type_vehicle: 'both',
        point_cost: 100, // Required for reward vouchers
        total_redemption_limit: 100,
        user_redemption_limit: 1
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showHelp, setShowHelp] = useState<boolean>(false);

    // Vehicle type options
    const vehicleTypeOptions = [
        { label: 'Tất cả phương tiện', value: 'both' },
        { label: 'Ô tô', value: 'car' },
        { label: 'Xe máy', value: 'motorbike' }
    ];

    // Initialize form when voucher changes
    useEffect(() => {
        if (voucher && isEdit) {
            // Edit mode - populate form with voucher data
            setFormData({
                title: voucher.title || '',
                description: voucher.description || '',
                code: voucher.code || '',
                discount_type: voucher.discount_value ? 'value' : 'percentage',
                discount_value: voucher.discount_value || undefined,
                discount_percentage: voucher.discount_percentage || undefined,
                max_discount: voucher.max_discount || undefined,
                start_date: new Date(voucher.start_date),
                end_date: new Date(voucher.end_date),
                max_usage: voucher.max_usage,
                minimum_amount: voucher.minimum_amount || undefined,
                voucher_type_vehicle: voucher.voucher_type_vehicle || 'both',
                point_cost: voucher.point_cost || 100,
                total_redemption_limit: voucher.total_redemption_limit || undefined,
                user_redemption_limit: voucher.user_redemption_limit || undefined
            });
        } else if (!isEdit) {
            // Create mode - reset to defaults
            setFormData({
                title: '',
                description: '',
                code: '',
                discount_type: 'percentage',
                discount_percentage: 10,
                max_discount: undefined,
                start_date: new Date(),
                end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                max_usage: 1,
                voucher_type_vehicle: 'both',
                point_cost: 100,
                total_redemption_limit: 100,
                user_redemption_limit: 1
            });
        }
        setErrors({});
    }, [voucher, isEdit]); // Removed 'visible' from dependencies

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Validate title
        if (!formData.title || formData.title.trim().length === 0) {
            newErrors.title = 'Tiêu đề là bắt buộc';
        } else if (formData.title.length < 3) {
            newErrors.title = 'Tiêu đề phải có ít nhất 3 ký tự';
        } else if (formData.title.length > 100) {
            newErrors.title = 'Tiêu đề không được quá 100 ký tự';
        }

        // Validate description
        if (!formData.description || formData.description.trim().length === 0) {
            newErrors.description = 'Mô tả là bắt buộc';
        } else if (formData.description.length < 10) {
            newErrors.description = 'Mô tả phải có ít nhất 10 ký tự';
        } else if (formData.description.length > 1000) {
            newErrors.description = 'Mô tả không được quá 1000 ký tự';
        }

        // Validate code
        if (!formData.code || formData.code.trim().length === 0) {
            newErrors.code = 'Mã code là bắt buộc';
        } else if (formData.code.length < 3) {
            newErrors.code = 'Mã code phải có ít nhất 3 ký tự';
        } else if (formData.code.length > 20) {
            newErrors.code = 'Mã code không được quá 20 ký tự';
        } else if (!/^[A-Z0-9_-]+$/i.test(formData.code)) {
            newErrors.code = 'Mã code chỉ được chứa chữ cái, số, dấu gạch dưới và dấu gạch ngang';
        }

        // Validate discount
        if (formData.discount_type === 'value') {
            if (!formData.discount_value || formData.discount_value <= 0) {
                newErrors.discount_value = 'Giá trị giảm giá phải lớn hơn 0';
            }
        } else {
            if (!formData.discount_percentage || formData.discount_percentage <= 0 || formData.discount_percentage > 100) {
                newErrors.discount_percentage = 'Phần trăm giảm giá phải từ 1% đến 100%';
            }
            // Validate max_discount when using percentage discount
            if (!formData.max_discount || formData.max_discount <= 0) {
                newErrors.max_discount = 'Số tiền giảm tối đa là bắt buộc khi chọn giảm theo phần trăm';
            }
        }

        // Validate dates
        if (!formData.start_date) {
            newErrors.start_date = 'Ngày bắt đầu là bắt buộc';
        }
        if (!formData.end_date) {
            newErrors.end_date = 'Ngày kết thúc là bắt buộc';
        }
        if (formData.start_date && formData.end_date && formData.start_date >= formData.end_date) {
            newErrors.end_date = 'Ngày kết thúc phải sau ngày bắt đầu';
        }

        // Validate usage limits
        if (formData.max_usage < 1) {
            newErrors.max_usage = 'Số lần sử dụng tối đa phải ít nhất là 1';
        }

        if (formData.total_redemption_limit && formData.total_redemption_limit < 1) {
            newErrors.total_redemption_limit = 'Giới hạn tổng số lần sử dụng phải ít nhất là 1';
        }

        if (formData.user_redemption_limit && formData.user_redemption_limit < 1) {
            newErrors.user_redemption_limit = 'Số lần đổi tối đa của mỗi người dùng phải ít nhất là 1';
        }

        // Validate minimum amount
        if (formData.minimum_amount && formData.minimum_amount < 0) {
            newErrors.minimum_amount = 'Số tiền tối thiểu không được âm';
        }

        // Validate point cost - REQUIRED for reward vouchers
        if (!formData.point_cost || formData.point_cost <= 0) {
            newErrors.point_cost = 'Chi phí điểm là bắt buộc và phải lớn hơn 0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            const submitData: CreateVoucherRequest | UpdateVoucherRequest = {
                voucher_type: 'reward',
                title: formData.title.trim(),
                description: formData.description.trim(),
                code: formData.code.trim().toUpperCase(), // Ensure uppercase for consistency
                discount_value: formData.discount_type === 'value' ? formData.discount_value : undefined,
                discount_percentage: formData.discount_type === 'percentage' ? formData.discount_percentage : undefined,
                max_discount: formData.discount_type === 'percentage' ? formData.max_discount : undefined,
                start_date: formData.start_date!.toISOString(),
                end_date: formData.end_date!.toISOString(),
                max_usage: formData.max_usage,
                minimum_amount: formData.minimum_amount,
                voucher_type_vehicle: formData.voucher_type_vehicle,
                point_cost: formData.point_cost,
                total_redemption_limit: formData.total_redemption_limit,
                user_redemption_limit: formData.user_redemption_limit,
                ...(voucher && { id: voucher.id })
            };

            await onSubmit(submitData);
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const dialogHeader = voucher ? 'Chỉnh sửa voucher đổi điểm' : 'Tạo voucher đổi điểm mới';

    // Custom header template with help button
    const headerTemplate = () => {
        return (
            <div className="dialog-header-custom">
                <span className="dialog-title">{dialogHeader}</span>
                <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setShowHelp(true)}
                    className="help-button"
                >
                    <i className="pi pi-question-circle"></i>
                </Button>
            </div>
        );
    };

    return (
        <>
            <Dialog
                header={headerTemplate}
                visible={visible}
                onHide={onHide}
                modal
                className="voucher-reward-form-dialog"
                style={{ width: '700px' }}
                footer={
                    <div className="form-footer">
                        <Button
                            variant="secondary"
                            onClick={onHide}
                            disabled={loading}
                        >
                            Hủy
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            loading={loading}
                        >
                            {voucher ? 'Cập nhật' : 'Tạo voucher'}
                        </Button>
                    </div>
                }
            >
                <div className="voucher-reward-form">
                    {/* Title and Description */}
                    <div className="form-section">
                        <h3>Thông tin cơ bản</h3>

                        <div className="form-field">
                            <label>Tiêu đề *</label>
                            <InputText
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="VD: Voucher đổi điểm tháng 12"
                                className={errors.title ? 'p-invalid' : ''}
                                maxLength={100}
                            />
                            {errors.title && (
                                <span className="error-text">{errors.title}</span>
                            )}
                            <small className="form-help">
                                Tiêu đề voucher hiển thị cho người dùng (3-100 ký tự)
                            </small>
                        </div>

                        <div className="form-field">
                            <label>Mô tả chi tiết *</label>
                            <Editor
                                value={formData.description}
                                onTextChange={(e) => setFormData(prev => ({ ...prev, description: e.htmlValue || '' }))}
                                placeholder="Nhập mô tả chi tiết về voucher..."
                                className={errors.description ? 'p-invalid' : ''}
                                style={{ height: '400px' }}
                            />
                            {errors.description && (
                                <span className="error-text">{errors.description}</span>
                            )}
                            <small className="form-help">
                                Mô tả chi tiết về voucher, điều kiện sử dụng, v.v. (10-1000 ký tự)
                            </small>
                        </div>
                    </div>

                    {/* Code Configuration */}
                    <div className="form-section">
                        <h3>Mã voucher</h3>

                        <div className="form-field">
                            <label>Mã code voucher *</label>
                            <InputText
                                value={formData.code}
                                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                                placeholder="VD: REWARD100, POINT50, EXCHANGE20"
                                className={errors.code ? 'p-invalid' : ''}
                                maxLength={20}
                            />
                            {errors.code && (
                                <span className="error-text">{errors.code}</span>
                            )}
                            <small className="form-help">
                                Mã code phải duy nhất, chỉ chứa chữ cái, số, dấu gạch dưới và dấu gạch ngang (3-20 ký tự)
                            </small>
                        </div>
                    </div>



                    {/* Point Cost Configuration */}
                    <div className="form-section">
                        <h3>Chi phí điểm *</h3>

                        <div className="form-field">
                            <label>Số điểm cần thiết để đổi voucher *</label>
                            <InputNumber
                                value={formData.point_cost}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, point_cost: e.value || 100 }))}
                                min={1}
                                placeholder="Nhập số điểm"
                                className={errors.point_cost ? 'p-invalid' : ''}
                            />
                            {errors.point_cost && (
                                <span className="error-text">{errors.point_cost}</span>
                            )}
                            <small className="form-help">
                                Voucher đổi điểm bắt buộc phải có chi phí điểm lớn hơn 0
                            </small>
                        </div>
                    </div>



                    {/* Discount Configuration */}
                    <div className="form-section">
                        <h3>Cấu hình giảm giá</h3>

                        <div className="discount-type-group">
                            <div className="radio-option">
                                <RadioButton
                                    inputId="discount-percentage"
                                    name="discount_type"
                                    value="percentage"
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        discount_type: e.value,
                                        discount_value: undefined,
                                        max_discount: prev.max_discount || 50000 // Set default max discount
                                    }))}
                                    checked={formData.discount_type === 'percentage'}
                                />
                                <label htmlFor="discount-percentage">Giảm theo phần trăm (%)</label>
                            </div>

                            <div className="radio-option">
                                <RadioButton
                                    inputId="discount-value"
                                    name="discount_type"
                                    value="value"
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        discount_type: e.value,
                                        discount_percentage: undefined,
                                        max_discount: undefined // Clear max discount for fixed amount
                                    }))}
                                    checked={formData.discount_type === 'value'}
                                />
                                <label htmlFor="discount-value">Giảm theo số tiền cố định (VNĐ)</label>
                            </div>
                        </div>

                        {formData.discount_type === 'percentage' ? (
                            <>
                                <div className="form-field">
                                    <label>Phần trăm giảm giá *</label>
                                    <InputNumber
                                        value={formData.discount_percentage}
                                        onValueChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: e.value || 0 }))}
                                        suffix="%"
                                        min={1}
                                        max={100}
                                        className={errors.discount_percentage ? 'p-invalid' : ''}
                                    />
                                    {errors.discount_percentage && (
                                        <span className="error-text">{errors.discount_percentage}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <label>Số tiền giảm tối đa *</label>
                                    <InputNumber
                                        value={formData.max_discount}
                                        onValueChange={(e) => setFormData(prev => ({ ...prev, max_discount: e.value || 0 }))}
                                        mode="currency"
                                        currency="VND"
                                        locale="vi-VN"
                                        min={1000}
                                        placeholder="Nhập số tiền giảm tối đa"
                                        className={errors.max_discount ? 'p-invalid' : ''}
                                    />
                                    {errors.max_discount && (
                                        <span className="error-text">{errors.max_discount}</span>
                                    )}
                                    <small className="field-help">
                                        Khi giảm theo phần trăm, cần giới hạn số tiền giảm tối đa để kiểm soát chi phí
                                    </small>
                                </div>
                            </>
                        ) : (
                            <div className="form-field">
                                <label>Giá trị giảm giá *</label>
                                <InputNumber
                                    value={formData.discount_value}
                                    onValueChange={(e) => setFormData(prev => ({ ...prev, discount_value: e.value || 0 }))}
                                    mode="currency"
                                    currency="VND"
                                    locale="vi-VN"
                                    min={1000}
                                    className={errors.discount_value ? 'p-invalid' : ''}
                                />
                                {errors.discount_value && (
                                    <span className="error-text">{errors.discount_value}</span>
                                )}
                            </div>
                        )}

                        <div className="form-field">
                            <label>Số tiền tối thiểu để áp dụng</label>
                            <InputNumber
                                value={formData.minimum_amount}
                                onChange={(e) => setFormData(prev => ({ ...prev, minimum_amount: e.value || undefined }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                placeholder="Không giới hạn"
                                className={errors.minimum_amount ? 'p-invalid' : ''}
                            />
                            {errors.minimum_amount && (
                                <span className="error-text">{errors.minimum_amount}</span>
                            )}
                        </div>
                    </div>



                    {/* Date Configuration */}
                    <div className="form-section">
                        <h3>Thời gian áp dụng</h3>

                        <div className="date-range">
                            <div className="form-field">
                                <label>Ngày bắt đầu *</label>
                                <Calendar
                                    value={formData.start_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.value as Date }))}
                                    showTime
                                    hourFormat="24"
                                    dateFormat="dd/mm/yy"
                                    className={errors.start_date ? 'p-invalid' : ''}
                                />
                                {errors.start_date && (
                                    <span className="error-text">{errors.start_date}</span>
                                )}
                            </div>

                            <div className="form-field">
                                <label>Ngày kết thúc *</label>
                                <Calendar
                                    value={formData.end_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.value as Date }))}
                                    showTime
                                    hourFormat="24"
                                    dateFormat="dd/mm/yy"
                                    className={errors.end_date ? 'p-invalid' : ''}
                                />
                                {errors.end_date && (
                                    <span className="error-text">{errors.end_date}</span>
                                )}
                            </div>
                        </div>
                    </div>



                    {/* Usage Limits */}
                    <div className="form-section">
                        <h3>Giới hạn sử dụng và đổi điểm</h3>

                        <div className="form-field">
                            <label>Số lần sử dụng tối đa mỗi người *</label>
                            <InputNumber
                                value={formData.max_usage}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, max_usage: e.value || 1 }))}
                                min={1}
                                className={errors.max_usage ? 'p-invalid' : ''}
                            />
                            {errors.max_usage && (
                                <span className="error-text">{errors.max_usage}</span>
                            )}
                            <small className="form-help">
                                Số lần mỗi người có thể sử dụng voucher sau khi đã đổi từ điểm
                            </small>
                        </div>

                        <div className="form-field">
                            <label>Tổng số lần đổi toàn hệ thống</label>
                            <InputNumber
                                value={formData.total_redemption_limit}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, total_redemption_limit: e.value || undefined }))}
                                min={1}
                                placeholder="Không giới hạn"
                                className={errors.total_redemption_limit ? 'p-invalid' : ''}
                            />
                            {errors.total_redemption_limit && (
                                <span className="error-text">{errors.total_redemption_limit}</span>
                            )}
                            <small className="form-help">
                                Giới hạn tổng số lần voucher này có thể được đổi bởi tất cả người dùng
                            </small>
                        </div>

                        <div className="form-field">
                            <label>Số lần đổi tối đa của mỗi người dùng</label>
                            <InputNumber
                                value={formData.user_redemption_limit}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, user_redemption_limit: e.value || undefined }))}
                                min={1}
                                placeholder="Không giới hạn"
                                className={errors.user_redemption_limit ? 'p-invalid' : ''}
                            />
                            {errors.user_redemption_limit && (
                                <span className="error-text">{errors.user_redemption_limit}</span>
                            )}
                            <small className="form-help">
                                Giới hạn số lần một người dùng có thể đổi voucher này từ điểm tích lũy
                            </small>
                        </div>
                    </div>



                    {/* Vehicle Type */}
                    <div className="form-section">
                        <h3>Phương tiện áp dụng</h3>

                        <div className="form-field">
                            <label>Loại phương tiện *</label>
                            <Dropdown
                                value={formData.voucher_type_vehicle}
                                onChange={(e) => setFormData(prev => ({ ...prev, voucher_type_vehicle: e.value }))}
                                options={vehicleTypeOptions}
                                placeholder="Chọn loại phương tiện"
                            />
                        </div>
                    </div>

                    {/* Information Notes */}
                    <div className="form-notes">
                        <div className="note-item">
                            <i className="pi pi-info-circle"></i>
                            <span>Voucher đổi điểm bắt buộc phải có chi phí điểm lớn hơn 0</span>
                        </div>
                        <div className="note-item">
                            <i className="pi pi-info-circle"></i>
                            <span>Mã code phải là duy nhất trong toàn hệ thống</span>
                        </div>
                        <div className="note-item">
                            <i className="pi pi-info-circle"></i>
                            <span>Số lần đổi tối đa giới hạn việc một người dùng có thể đổi voucher này bao nhiêu lần</span>
                        </div>
                        <div className="note-item">
                            <i className="pi pi-info-circle"></i>
                            <span>Nên cân nhắc tỷ lệ điểm/giá trị để tạo động lực cho người dùng</span>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* Help Dialog */}
            <Dialog
                header="Hướng dẫn Voucher Đổi Điểm"
                visible={showHelp}
                onHide={() => setShowHelp(false)}
                modal
                className="voucher-help-dialog"
                style={{ width: '700px' }}
            >
                <div className="help-content">
                    <div className="help-section">
                        <h3>Voucher Đổi Điểm là gì?</h3>
                        <p>Voucher đổi điểm là loại voucher đặc biệt trong chương trình tích điểm thưởng. Người dùng cần tích lũy đủ điểm để có thể đổi lấy voucher này.</p>
                    </div>

                    <div className="help-section">
                        <h3>Các thông tin cần thiết:</h3>
                        <ul className="help-list">
                            <li><strong>Mã code:</strong> Mã code cố định và duy nhất cho voucher (3-20 ký tự).</li>
                            <li><strong>Chi phí điểm:</strong> Số điểm tích lũy cần thiết để đổi voucher (bắt buộc &gt; 0).</li>
                            <li><strong>Loại giảm giá:</strong> Chọn giảm theo phần trăm (%) hoặc số tiền cố định (VNĐ).</li>
                            <li><strong>Thời gian hiệu lực:</strong> Ngày bắt đầu và kết thúc voucher.</li>
                            <li><strong>Số lần sử dụng tối đa:</strong> Số lần mỗi người có thể sử dụng voucher sau khi đã đổi.</li>
                            <li><strong>Tổng số lần đổi:</strong> Giới hạn tổng số lần voucher này có thể được đổi bởi tất cả người dùng.</li>
                            <li><strong>Số lần đổi của mỗi người:</strong> Giới hạn số lần một người dùng có thể đổi voucher này từ điểm.</li>
                            <li><strong>Phương tiện áp dụng:</strong> Voucher áp dụng cho ô tô, xe máy, hoặc cả hai.</li>
                        </ul>
                    </div>

                    <div className="help-section">
                        <h3>Cách thức hoạt động:</h3>
                        <ul className="help-list">
                            <li>Admin tạo voucher đổi điểm với chi phí điểm cụ thể.</li>
                            <li>Voucher hiển thị trong danh sách đổi điểm của ứng dụng người dùng.</li>
                            <li>Người dùng có đủ điểm có thể đổi voucher (trừ điểm, nhận mã voucher).</li>
                            <li>Sau khi đổi thành công, người dùng có mã voucher để sử dụng khi đặt xe.</li>
                            <li>Mỗi lần sử dụng voucher sẽ được tính vào giới hạn sử dụng (max_usage).</li>
                            <li>Mỗi lần đổi voucher sẽ được tính vào giới hạn đổi điểm (user_redemption_limit).</li>
                        </ul>
                    </div>

                    <div className="help-section">
                        <h3>Lưu ý quan trọng:</h3>
                        <div className="help-notes">
                            <div className="help-note warning">
                                <i className="pi pi-exclamation-triangle"></i>
                                <span>Chi phí điểm phải lớn hơn 0. Voucher miễn phí không phù hợp cho chương trình đổi điểm.</span>
                            </div>
                            <div className="help-note info">
                                <i className="pi pi-info-circle"></i>
                                <span>Nên cân nhắc tỷ lệ điểm/giá trị để tạo động lực tích điểm cho người dùng.</span>
                            </div>
                            <div className="help-note info">
                                <i className="pi pi-info-circle"></i>
                                <span>Voucher đổi điểm sẽ hiển thị trong mục "Đổi điểm" của ứng dụng người dùng.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default VoucherRewardForm;
